import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import "./styles/app.css";
import { buildRouter } from "./router";
import { sessionQuery, themeQuery } from "./api/queries";
import { describeFailure } from "./api/mutationErrors";
import { pushToast } from "./components/ui/toastStore";
import { ThemeProvider } from "./theme/ThemeProvider";
import { initColorScheme } from "./theme/colorScheme";

// Apply the stored light/dark/system preference before the first paint.
initColorScheme();

/**
 * Every mutation failure in the application is announced (backlog item 11).
 *
 * `MutationCache.onError` is the single point all 66 mutations already pass
 * through, so a toast here is the **default** and silence is what has to be
 * asked for — `meta: { errorSurface: "inline" }` on the mutation, for the
 * surfaces that render the error themselves. The inversion is the whole point:
 * the 204 delete bug survived because a failure with no surface is
 * indistinguishable from a no-op, and a per-call-site convention would have
 * needed the next person to remember.
 *
 * Chat run failures do not arrive here. They are SSE `error` events on the run
 * stream, rendered in the thread that produced them, and routing them through
 * this as well would report every one of them twice.
 */
const queryClient: QueryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.errorSurface === "inline") return;
      const notice = describeFailure(error);
      pushToast(notice);
      // An expired session makes every later request fail the same way, so the
      // cached "you are unlocked" is now a lie. Invalidating it lets the next
      // navigation redirect through the router's own guard — deliberately not
      // a redirect from here, which would throw someone off a half-written
      // form and take the edits with it.
      if (notice.code === "unauthorized") {
        void queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      }
    },
  }),
});

// The theme is content the whole shell depends on; warm it immediately.
void queryClient.prefetchQuery(themeQuery);

const router = buildRouter(queryClient);

const container = document.getElementById("root");
if (!container) throw new Error("Root container missing from index.html");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
